import { Loader2 } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

interface LazyDashboardSectionProps {
  children: ReactNode;
  className?: string;
  /** Prefetch when the sentinel is this far from the viewport. */
  rootMargin?: string;
}

/**
 * Mounts dashboard widgets only when scrolled near the viewport so first paint
 * does not fan out every below-the-fold metric request at once (ENR-2471).
 */
export function LazyDashboardSection({
  children,
  className,
  rootMargin = '240px',
}: LazyDashboardSectionProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || isVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [isVisible, rootMargin]);

  return (
    <div ref={sentinelRef} className={className}>
      {isVisible ? (
        children
      ) : (
        <div className='flex min-h-[160px] items-center justify-center py-12'>
          <Loader2 className='size-8 animate-spin text-muted-foreground' />
        </div>
      )}
    </div>
  );
}
